import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, Typography, Button, Result, Alert } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { AuthService } from '../service/auth.service';
import { useTheme } from '../components/ThemeProvider';

const { Paragraph } = Typography;

const EmailVerification: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
    const [message, setMessage] = useState<string>('');
    const { isDark } = useTheme();
    const hasVerified = useRef(false);

    useEffect(() => {
        const verifyEmail = async () => {
            // Prevent multiple API calls
            if (hasVerified.current) {
                return;
            }

            const token = searchParams.get('token');
            
            if (!token) {
                setStatus('error');
                setMessage('No verification token provided');
                hasVerified.current = true;
                return;
            }

            try {
                hasVerified.current = true;
                await AuthService.verifyEmail(token);
                setStatus('success');
                setMessage('Email verified successfully!');
            } catch (error: any) {
                setStatus('error');
                setMessage(error.response?.data?.message || 'Email verification failed');
            }
        };

        verifyEmail();
    }, [searchParams, navigate]);

    const renderContent = () => {
        switch (status) {
            case 'loading':
                return (
                    <Result
                        icon={<LoadingOutlined style={{ fontSize: 64, color: '#1890ff' }} />}
                        title="Verifying your email..."
                        subTitle="Please wait while we verify your email address."
                    />
                );
            case 'success':
                return (
                    <Result
                        status="success"
                        icon={<CheckCircleOutlined />}
                        title="Email Verified Successfully!"
                        subTitle={
                            <div>
                                <Paragraph style={{ marginBottom: '16px' }}>
                                    Your email has been verified. You can now log in to the MetaGame News app.
                                </Paragraph>
                                <Alert
                                    message="You can close this page and go back to the app to log in."
                                    type="info"
                                    showIcon
                                    style={{ marginBottom: '16px' }}
                                />
                            </div>
                        }
                        extra={[
                            <Button
                                type="primary"
                                key="home"
                                onClick={() => navigate('/')}
                            >
                                Browse News
                            </Button>
                        ]}
                    />
                );
            case 'error':
                return (
                    <Result
                        status="error"
                        icon={<CloseCircleOutlined />}
                        title="Verification Failed"
                        subTitle={message}
                        extra={[
                            <Button
                                type="primary"
                                key="home"
                                onClick={() => navigate('/')}
                            >
                                Go to Home
                            </Button>
                        ]}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <div style={{
            minHeight: '100vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: isDark ? '#141414' : '#f0f2f5',
            padding: '24px'
        }}>
            <Card
                style={{
                    width: '100%',
                    maxWidth: '500px',
                    backgroundColor: isDark ? '#1f1f1f' : '#fff',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                }}
            >
                {renderContent()}
            </Card>
        </div>
    );
};

export default EmailVerification; 